import { useTranslation } from '@exam-room/translations';
import { Dialog } from '@headlessui/react';
import { useEffect, useRef, useState } from 'react';

import { Button, Modal } from '../base';
import { CancelOutlinedIcon } from '../icons';
import LoadingSpinner from '../loading-spinner';

interface ExamCardExpiredModalProps {
  open: boolean;
  onConfirm: () => void;
  isLoading?: boolean;
  countdownTime?: number;
}

export default function ExamCardExpiredModal({
  open,
  onConfirm,
  isLoading,
  countdownTime = 120,
}: ExamCardExpiredModalProps) {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(countdownTime);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setCountdown(countdownTime);
      hasTriggeredRef.current = false;
      return;
    }

    const interval = window.setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0 && !hasTriggeredRef.current) {
          hasTriggeredRef.current = true;
          onConfirm();
          window.clearInterval(interval);
        }
        return next;
      });
    }, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [open, countdownTime, onConfirm]);

  const displayCountdown = Math.max(0, countdown);

  return (
    <Modal open={open} className='max-w-xs'>
      <div className='flex flex-col items-center gap-6 text-center'>
        <div className='flex flex-col items-center gap-2 text-center'>
          <div className='flex size-20 items-center justify-center rounded-full bg-red-50'>
            <CancelOutlinedIcon className='text-[60px] text-red-500' />
          </div>
          <Dialog.Title as='h3' className='text-2xl font-bold'>
            {t('examCardExpiredModal.title')}
          </Dialog.Title>
          <Dialog.Description className='text-text-secondary whitespace-pre-line'>
            {t('examCardExpiredModal.description')}
          </Dialog.Description>
        </div>
        <Button onClick={onConfirm} disabled={isLoading}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            `${t('examCardExpiredModal.confirmButton')} (${displayCountdown}s)`
          )}
        </Button>
      </div>
    </Modal>
  );
}
